import { useEffect, useRef } from 'react';
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

interface MapEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  height?: string;
}

// Fix default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function MapEditor({ value, onChange, readOnly = false, height = '400px' }: MapEditorProps) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [47.5, 14.5],
      zoom: 7,
    });

    L.tileLayer('/tiles/styles/bright/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
      maxNativeZoom: 14,
    }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    if (value) {
      try {
        const data = JSON.parse(value);
        L.geoJSON(data, {
          pointToLayer: (_feature, latlng) =>
            L.marker(latlng, {
              icon: L.icon({
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
              }),
            }),
        }).eachLayer((layer) => drawnItems.addLayer(layer));

        if (drawnItems.getBounds().isValid()) {
          map.fitBounds(drawnItems.getBounds());
        }
      } catch { /* ignore */ }
    }

    if (!readOnly) {
      const drawControl = new (L.Control as any).Draw({
        edit: { featureGroup: drawnItems },
        draw: {
          polygon: true,
          polyline: true,
          rectangle: true,
          circle: false,
          circlemarker: false,
          marker: true,
        },
      });
      map.addControl(drawControl);

      const updateValue = () => {
        const geojson = drawnItems.toGeoJSON() as GeoJSON.FeatureCollection;
        geojson.features = geojson.features.map((f: any) => {
          const { id, ...rest } = f;
          return rest;
        });
        onChange(JSON.stringify(geojson));
      };

      map.on(L.Draw.Event.CREATED, (e: any) => {
        drawnItems.addLayer(e.layer);
        updateValue();
      });
      map.on(L.Draw.Event.EDITED, () => updateValue());
      map.on(L.Draw.Event.DELETED, () => updateValue());
    }

    mapRef.current = map;
    setTimeout(function(){ map.invalidateSize(); }, 200);

    return function cleanup() {
      map.remove();
      mapRef.current = null;
    };
  }, [readOnly]);

  const featureCount = (() => {
    try {
      const data = JSON.parse(value);
      return data.features?.length || 0;
    } catch { return 0; }
  })();

  return (
    <>
      <style>{".leaflet-pane img,.leaflet-tile,.leaflet-marker-icon,.leaflet-marker-shadow{max-width:none!important}.leaflet-container{width:100%!important;height:100%!important}"}</style>
    <div>
      <div ref={containerRef} style={{ height, width: '100%', borderRadius: '0.5rem', zIndex: 1 }} />
      {!readOnly && featureCount > 0 && (
        <p className="text-xs text-green-600 mt-1">✅ {featureCount} Geometrie(n) gespeichert</p>
      )}
    </div>
    </>
  );
}
